import React, { useCallback, useRef } from 'react';
import { CopilotComposer } from '@/experiences/sales-portal/domain/copilot/screens/copilot-chat/components/copilot-composer';
import { CopilotEmptyState } from '@/experiences/sales-portal/domain/copilot/screens/copilot-chat/components/copilot-empty-state';
import {
  CopilotMessage,
  CopilotMessageStatus,
  useCopilotSessionStore,
} from '@/experiences/sales-portal/domain/copilot/screens/copilot-chat/blocs/stores/use-copilot-session-store';
import { CopilotMessageBubble } from '@/experiences/sales-portal/domain/copilot/screens/copilot-chat/components/copilot-message-bubble';
import { CopilotNewSessionButton } from '@/experiences/sales-portal/domain/copilot/screens/copilot-chat/components/copilot-new-session-button';
import { CopilotTypingBubble } from '@/experiences/sales-portal/domain/copilot/screens/copilot-chat/components/copilot-typing-bubble';
import { createStyles, useTheme } from '@/contexts/theme-context';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { ScrollView, View } from 'react-native';
import { useSendCopilotMessageMutator } from '@/experiences/sales-portal/domain/copilot/screens/copilot-chat/blocs/mutators/use-send-copilot-message-mutator';

const CONTENT_MAX_WIDTH = 750;

export const CopilotChatContent: React.FC = () => {
  const [styles] = useStyles();
  const theme = useTheme();
  const scrollRef = useRef<ScrollView>(null);

  const messages = useCopilotSessionStore((s) => s.messages);
  const reset = useCopilotSessionStore((s) => s.reset);
  const isAwaitingReply = messages.some(
    (m) => m.status === CopilotMessageStatus.Sending,
  );
  const isConsecutiveMessage = (i: number, m: CopilotMessage) =>
    i > 0 && messages[i - 1]?.role === m.role;

  const scrollToEnd = useCallback(() => {
    requestAnimationFrame(() => {
      scrollRef.current?.scrollToEnd({ animated: true });
    });
  }, []);

  const hasDoneInitialScroll = useRef(false);
  const handleContentSizeChange = useCallback(() => {
    if (hasDoneInitialScroll.current) {
      return;
    }
    hasDoneInitialScroll.current = true;
    scrollRef.current?.scrollToEnd({ animated: false });
  }, []);

  const { retry, send } = useSendCopilotMessageMutator({
    onMessagesChange: scrollToEnd,
  });

  return (
    <KeyboardAvoidingView
      behavior="padding"
      keyboardVerticalOffset={theme.sk.spacing.spaceMedium}
      style={styles.container}
    >
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        onContentSizeChange={handleContentSizeChange}
        ref={scrollRef}
        showsVerticalScrollIndicator={false}
        style={styles.scroll}
      >
        {messages.length === 0 ? (
          <CopilotEmptyState />
        ) : (
          messages.map((message, index) => (
            <CopilotMessageBubble
              key={message.id}
              message={message}
              onRetry={
                message.status === CopilotMessageStatus.Failed
                  ? () => {
                      void retry(message.id);
                    }
                  : undefined
              }
              // Bubbles on the same side of the chat only show the tail on the last bubble
              showTail={!isConsecutiveMessage(index, message)}
              // Bubbles on the same side of the chat are spaced tighter together,
              // whereas bubbles sent by different users are more separated
              style={
                isConsecutiveMessage(index, message)
                  ? styles.messageGrouped
                  : styles.messageSeparated
              }
            />
          ))
        )}
        {isAwaitingReply ? (
          <CopilotTypingBubble style={styles.messageSeparated} />
        ) : null}
      </ScrollView>
      <View style={styles.composer}>
        <CopilotComposer onSend={send} sending={isAwaitingReply} />
      </View>
      {messages.length > 0 ? <CopilotNewSessionButton onPress={reset} /> : null}
    </KeyboardAvoidingView>
  );
};

const useStyles = createStyles((theme) => ({
  composer: {
    marginBottom: theme.sk.spacing.spaceMedium,
    marginTop: theme.sk.spacing.spaceSmall,
    maxWidth: CONTENT_MAX_WIDTH,
    width: '100%',
  },
  container: {
    alignItems: 'center',
    flex: 1,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
  },
  messageGrouped: {
    marginTop: theme.sk.spacing.spaceXsmall,
  },
  messageSeparated: {
    marginTop: theme.sk.spacing.spaceMedium,
  },
  scroll: {
    flex: 1,
    maxWidth: CONTENT_MAX_WIDTH,
    width: '100%',
  },
  scrollContent: {
    alignItems: 'center',
    paddingBottom: theme.sk.spacing.spaceMedium,
  },
}));
